import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { useSelector } from 'react-redux';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { keys as incidentStrategyKeys } from './incidentStrategyHooks';
import { StrategyBase } from '../../../types/iapBaseTypes';

export type Strategy = StrategyBase & {
  id: UUID;
  group_guid: UUID;
  archived: boolean;
  tactics: UUID[];
};

export const keys = {
  base: 'strategies',
  get all() {
    return [keys.base];
  },
  get: function (group_guid: UUID, user_guid: UUID) {
    return [keys.base, group_guid, user_guid];
  },
} as const;

export const useStrategies = (isEnabled: boolean) => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.get(group_guid, user_guid),
    queryFn: async () => {
      const resp = await axios.get<Strategy[]>(
        `${config.apiGateway.ics_strategies}/${group_guid}`
      );
      return resp.data;
    },
    enabled: isEnabled,
  });
};

export type CreateStrategyReqData = StrategyBase & {
  incident_id?: UUID;
  objectives?: UUID[];
};

export const useCreateStrategy = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateStrategyReqData) => {
      return await axios.post(`${config.apiGateway.ics_strategies}`, {
        ...data,
        group_guid,
        user_guid,
      });
    },
    onSuccess: (_, reqObj) => {
      queryClient.invalidateQueries({
        queryKey: keys.get(group_guid, user_guid),
      });
      if (reqObj.incident_id) {
        queryClient.invalidateQueries({
          queryKey: incidentStrategyKeys.all,
        });
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export type EditStrategyReqData = StrategyBase & {
  id: UUID;
  archived: boolean;
};

export const useEditStrategy = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditStrategyReqData) => {
      const { id, ...updateData } = data;
      return await axios.put(`${config.apiGateway.ics_strategies}/${id}`, {
        ...updateData,
        group_guid,
        user_guid,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.get(group_guid, user_guid),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
